import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Login as LoginIcon,
} from '@mui/icons-material';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import PlatformFeatureGrid from '@components/landing/PlatformFeatureGrid';
import SportDiscoveryGrid from '@components/landing/SportDiscoveryGrid';
import LandingConvertSection from '@components/landing/LandingConvertSection';
import { CORE_SPORTS, toSportSlug } from '@utils/sports';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const headerSports = CORE_SPORTS;

  return (
    <Box>
      <AppBar position="sticky" sx={{ bgcolor: '#ffffff', boxShadow: 1 }}>
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <EncycdataLogo height={20} />
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: '#1f2937', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              EncycGames
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
              {headerSports.map((sportName) => (
                <Button
                  key={sportName}
                  size="small"
                  variant="text"
                  onClick={() => navigate(`/sport/${toSportSlug(sportName)}`)}
                  sx={{ color: '#1f2937', textTransform: 'none', minWidth: 'auto', px: 1 }}
                >
                  {sportName}
                </Button>
              ))}
            </Stack>
          </Box>
          <Button
            variant="text"
            onClick={() => navigate('/sponsors')}
            sx={{ mr: 1, color: '#7c3aed', fontWeight: 700, textTransform: 'none' }}
          >
            Sponsors
          </Button>
          <Button
            variant="text"
            onClick={() => navigate('/team')}
            sx={{ mr: 1, color: '#1976d2' }}
          >
            Team
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/pricing')}
            sx={{ mr: 1, borderColor: '#1976d2', color: '#1976d2' }}
          >
            Pricing
          </Button>
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={() => navigate('/login')}
            sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
          >
            Login
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 55%, #1d4ed8 100%)',
          color: 'white',
          py: { xs: 7, md: 10 },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.12,
            backgroundImage: 'url(/images/Badminton-Smash-Contest.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center right',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(15,23,42,0.95) 0%, rgba(15,23,42,0.75) 50%, rgba(29,78,216,0.4) 100%)',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Stack spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }} textAlign={{ xs: 'center', md: 'left' }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                maxWidth: 720,
                lineHeight: 1.15,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Rank skills. Learn deeply. Compete in contests.
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, opacity: 0.92, maxWidth: 600, lineHeight: 1.5 }}>
              EncycGames empowers coaches, youth and parents with structured rubrics, video learning, rankings and contests across sports.
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'center', md: 'flex-start' }}>
              {['Rubrics', 'Learning', 'Rankings', 'Contests', 'Academies'].map((pill) => (
                <Chip
                  key={pill}
                  label={pill}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#f8fafc', fontWeight: 700 }}
                />
              ))}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register/player')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  px: 3,
                  bgcolor: '#f59e0b',
                  color: '#0f172a',
                  '&:hover': { bgcolor: '#fbbf24' },
                }}
              >
                Register free
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Sign in
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Main content */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <PlatformFeatureGrid />
        <SportDiscoveryGrid />
        <LandingConvertSection />
      </Container>

      {/* Contact strip */}
      <Box sx={{ bgcolor: '#f8fafc', py: 5, borderTop: '1px solid #e2e8f0' }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
            Need help onboarding your academy?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Our team can help you set up players, coaches and contest programs quickly.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center">
            <Button variant="contained" onClick={() => navigate('/team')} sx={{ textTransform: 'none', fontWeight: 700 }}>
              Contact Team
            </Button>
            <Button variant="outlined" onClick={() => navigate('/pricing')} sx={{ textTransform: 'none' }}>
              View Plans
            </Button>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ bgcolor: '#0f172a', color: '#e2e8f0', py: 2 }}>
        <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>Encycdata</Typography>
          <Typography variant="caption">EncycGames · Sports knowledge platform</Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
